import { useContext, useLayoutEffect, useState } from "react"
import { NHSApp } from "../types"
import { useNavigate } from "react-router-dom"
import { AuthenticationContext } from "../context/AuthenticationContext"
import { mockNHSJavascript } from "../utils/mockNHSJavascript"

/**
 * Gets the NHS App JS API from the global `window` object.
 *
 * See: https://nhsconnect.github.io/nhsapp-developer-documentation/js-api-specification/
 *
 * @returns {NHSApp | null} The NHSApp object from the global `window` object or null if it doesn't exist.
 */

export type NavigationOption = "home" | "appointments" | "back" | { relativePath: string } | "error"
export const useNhsAppJs = () => {
  const [nhs, setNhs] = useState<NHSApp | null>(null)
  const navigate = useNavigate()
  const { leaveSite } = useContext(AuthenticationContext)

  useLayoutEffect(() => {
    const localSetting = localStorage.getItem("IN_APP")
    if (localSetting) {
      setNhs(mockNHSJavascript(localSetting === "TRUE"))
    } else {
      setNhs(window.nhsapp ?? null)
    }
  }, [])

  const inApp = nhs?.tools.isOpenInNHSApp()
  const appHomePage = inApp ? nhs?.navigation.AppPage.HOME_PAGE : null
  const appReturnToPrevious = inApp ? nhs?.navigation.AppPage.GO_BACK : null
  const appNavigate = nhs?.navigation.goToPage

  const navigateToPage = (nextPage: NavigationOption) => {
    switch (nextPage) {
      case "back":
        navigate(-1)
        break
      case "appointments":
        inApp && appNavigate && appReturnToPrevious ? appNavigate(appReturnToPrevious) : navigate(-1)
        break
      case "home":
        inApp && appNavigate && appHomePage ? appNavigate(appHomePage) : leaveSite()
        break
      case "error":
        navigate("/error?msg=internal", { replace: true })
        break
      default:
        navigate(nextPage.relativePath, { relative: "path", replace: nextPage.relativePath.endsWith("complete") })
        break
    }
  }
  return { inApp, navigateToPage }
}
